import { v } from 'h3-valibot';
import type {
  CartFragment,
  CartItemsPageFragment,
  OrderAddress,
  ShippingCartAddressFragment
} from '~~/lib/Shop/generated/schema';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import type { ActionModel } from '~/stores/models';
import type { OptionalMoneyFragment } from '~/utils/shop/formatMoney';

export enum CartTypes {
  CART = 'cart',
  CUSTOMER_CART = 'customerCart',
  REQUEST_CART = 'requestCart'
}

export type CartType =
  CartTypes.CART | CartTypes.REQUEST_CART | CartTypes.CUSTOMER_CART;

export type CartWithItemsFragment = CartFragment & CartItemsPageFragment;

const navigationItemSchema = v.object({
  text: v.optional(v.string()),
  link: v.optional(
    v.object({
      uri: v.optional(v.unknown())
    })
  )
});

const modifyCartItemBySku = v.object({
  quantity: v.number(),
  sku: v.string(),
  config_id: v.optional(v.string())
});
const modifyCartItemByDecoratedProduct = v.object({
  quantity: v.number(),
  config_id: v.optional(v.string()),
  decoratedProduct: v.instance(DecoratedProduct),
  /**
   * Pre-resolved breadcrumb trail (e.g. from PDP or cart API). When set,
   * analytics prefers these labels over Magento category names.
   */
  breadcrumbs: v.optional(v.array(navigationItemSchema))
});

export type ModifyCartItemByDecoratedProduct = v.InferOutput<
  typeof modifyCartItemByDecoratedProduct
>;

/** Minimal cart line shape accepted by `mapCartItemsForAnalytics`. */
export type AnalyticsCartItemSource = {
  quantity?: number | null;
  decoratedProduct?: DecoratedProduct;
  breadcrumbs?: ModifyCartItemByDecoratedProduct['breadcrumbs'];
};

export const modifyCartItemSchema = v.union([
  modifyCartItemBySku,
  modifyCartItemByDecoratedProduct
]);

export type EComDataType = {
  currency?: string;
  value: number;
  items: ReadonlyArray<any>;
  ecom_source: string;
  finder?: string;
  finder_entry?: string;
  finder_session_id?: string;
  ab_session_id?: string;
  tax?: OptionalMoneyFragment;
  transaction_id?: string;
  coupon?: string;
  shipping?: ShippingCartAddressFragment | OrderAddress;
  payment_type?: string;
  checkout_step?: string;
  cart_id?: string;
};

export type ModifyCartItem = v.InferOutput<typeof modifyCartItemSchema>;

export type UpdateCartItem = {
  uid: string;
  quantity: number;
  config_id: string;
};

export type CartActionType =
  | 'mergeCarts'
  | 'addItems'
  | 'removeItem'
  | 'updateItems'
  | 'addShippingAddress'
  | 'addBillingAddress'
  | 'addDeliveryMethod'
  | 'setPaymentMethod'
  | 'setAdyenStateData'
  | 'addAdyenPaymentDetails'
  | 'loadAgreements'
  | 'addCoupon'
  | 'removeCoupon'
  | 'checkTaxExemption'
  | 'setTaxExemptionChoice'
  | 'uploadTaxExemption'
  | 'removeTaxExemption'
  | 'loadCart'
  | 'placeQuote'
  | 'placeNBQ'
  | 'placeOrder';

export type CartActionModel = ActionModel<CartActionType> & {
  /** When set, the generic cart success toast substitutes `{productName}`. */
  productName?: string;
  /** When true, success toasts for this action are skipped. */
  silent?: boolean;
};

export type RemoveFromCartOptions = {
  /** When true, the remove success toast is skipped. */
  silent?: boolean;
};

export type AddProductsOptions = {
  /**
   * The source the product was added from.
   * Used in analytic event. If not present, a default source
   * will be used.
   *
   * @see https://gcp.baslerweb.com/jira/browse/WEB2-2439
   */
  source?: string;
  /** When set, the generic cart success toast substitutes `{productName}`. */
  productName?: string;
};

export type UpdateProductsOptions = {
  /**
   * The source the cart update originated from.
   * Used in analytics events (e.g. productFinder removal attribution).
   */
  source?: string;
  /** When set, the generic cart success toast substitutes `{productName}`. */
  productName?: string;
};

export type LoadCartOptions = {
  currentPage?: number;
  pageSize?: number;
  /** When true, only toggles list skeleton state instead of global loading. */
  itemsPageLoading?: boolean;
};

export type CartStoreState = {
  /**
   * @deprecated use `$shopStore` from `useNuxtApp` instead.
   */
  store: string;
  /**
   * @deprecated use `$activeLocale` from `useNuxtApp` instead.
   */
  locale: string;
  lastAction: CartActionModel | null;
  /**
   * Loading indicator for __every__ async api action performed in this.
   * If a more detailed loading state is necessary, this could be moved to
   * a getter.
   */
  loading: boolean;
  /**
   * All available carts and **loaded** carts.
   * A cart might exist (cookie) but is not loaded. In that, the
   * value will be `null`!
   */
  carts: Record<CartType, CartFragment | null>;
  relevantCarts: Record<CartType, CartFragment | null>;
  activeCartType: CartType;
};
